import React, { type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faStore, faFileInvoiceDollar, faSignOutAlt, faRobot, faUserCheck, faPalette } from '@fortawesome/free-solid-svg-icons';

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="h-screen bg-gray-900 font-sans text-white selection:bg-pink-500/30 overflow-hidden">
      {/* Background gradients for premium feel */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px] mix-blend-screen" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-600/20 rounded-full blur-[120px] mix-blend-screen" />
      </div>

      <div data-ui="admin-layout" className="relative z-10 flex h-screen">
        {/* Admin Sidebar */}
        <aside data-ui="admin-navigation" className="w-64 shrink-0 h-full border-r border-white/10 bg-white/5 backdrop-blur-2xl flex flex-col overflow-y-auto">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-white">
              Taratic Admin
            </h2>
          </div>
          
          <nav className="flex-1 px-4 space-y-2 mt-4">
            <NavLink
              to="/admin/dashboard"
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-white/10 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <FontAwesomeIcon icon={faChartPie} className="w-5 h-5 mr-3" />
              Dashboard
            </NavLink>
            <NavLink
              to="/admin/design-system"
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-white/10 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <FontAwesomeIcon icon={faPalette} className="w-5 h-5 mr-3" />
              App Design
            </NavLink>
            <NavLink
              to="/admin/comercios"
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-white/10 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <FontAwesomeIcon icon={faStore} className="w-5 h-5 mr-3" />
              Comercios
            </NavLink>
            <NavLink
              to="/admin/bot-stats"
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-white/10 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <FontAwesomeIcon icon={faRobot} className="w-5 h-5 mr-3" />
              Bot Stats
            </NavLink>
            <NavLink
              to="/admin/available-users"
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive ? 'bg-white/10 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <FontAwesomeIcon icon={faUserCheck} className="w-5 h-5 mr-3" />
              Available Users
            </NavLink>
            <NavLink
              to="/admin/billing"
              className={({ isActive }) =>
                `flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-white/10 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`
              }
            >
              <FontAwesomeIcon icon={faFileInvoiceDollar} className="w-5 h-5 mr-3" />
              Billing
            </NavLink>
          </nav>

          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="w-5 h-5 mr-3" />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main data-ui="admin-main-content" className="flex-1 min-w-0 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
